<template>
    <v-badge
        color="transparent"
        :model-value="showNotificationBadge"
        offset-y="-2"
        offset-x="-1"
        class="mode-chip__badge"
        :class="cssClasses"
    >
        <notification-dialog
            v-if="shouldNotificationDialogBeAvailable"
            v-model="showNotificationDialog"
            :notification-messages="sortedNotificationMessages"
        />
        <v-sheet rounded :color="color" :border="border" height="30" min-width="32" class="mode-chip" :class="modeChipCssClasses">
            <v-row no-gutters class="d-flex flex-row align-center justify-center flex-nowrap">
                <v-col v-if="icon" cols="auto" class="d-flex align-center">
                    <v-icon :icon="icon" :size="iconSize" :color="iconColor" class="mode-chip__icon" />
                </v-col>
                <v-col v-if="chipTitle && !isCompact" cols="auto">
                    <div class="mode-chip__title" v-text="chipTitle"></div>
                </v-col>
            </v-row>
        </v-sheet>
        <template #badge>
            <div class="notification-icon--overlap position-absolute right-0">
                <notification-icon
                    v-for="notification in sortedNotificationMessages.toReversed()"
                    :key="notification.id"
                    :model-value="notification.type"
                    size="18"
                />
            </div>
        </template>
    </v-badge>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { NotificationMessageDto } from '@/types/webapi';
import NotificationDialog from './NotificationDialog.vue';
import { NotificationIcon } from '@/features/common/components';
import { sortBySeverity } from '../helpers';

interface Props {
    /**
     * The title of the mode chip.
     */
    title?: string;
    /**
     * The color of the mode chip.
     */
    color?: string;
    /**
     * The icon of the mode chip.
     */
    icon?: string;
    /**
     * The size of the icon.
     */
    iconSize?: string | number;
    /**
     * The color of the icon.
     */
    iconColor?: string;
    /**
     * Whether the mode chip is compact.
     */
    isCompact?: boolean;
    /**
     * Whether the mode chip is fixed size.
     */
    fixedSize?: boolean;
    /**
     * The notification messages.
     */
    notificationMessages?: NotificationMessageDto[];
    /**
     * Whether the mode chip is active.
     */
    activeSummary?: boolean;
}

const props = withDefaults(defineProps<Props>(), { iconSize: 24, notificationMessages: () => [] });
const iconSize = computed(() => Number(props.iconSize));
const border = computed(() => (!props.color || props.color === 'transparent' || props.color === 'white' ? 'sm' : undefined));
const breakablePrefixes = ['SK', 'FB', 'VY'];
const showNotificationBadge = computed(() => props.notificationMessages !== undefined && props.notificationMessages?.length > 0);
const showNotificationDialog = ref(false);
const shouldNotificationDialogBeAvailable = computed(
    () => props.notificationMessages !== undefined && props.notificationMessages?.length > 0 && props.activeSummary
);
const sortedNotificationMessages = computed(() => [...props.notificationMessages].toSorted(sortBySeverity));

const chipTitle = computed(() => {
    let t = props.title ?? '';
    if (!props.fixedSize || t.length < 4) return t;

    return breakablePrefixes.some(p => t.indexOf(p) === 0) ? `${t.substring(0, 2)} ${t.substring(2)}` : t;
});

const cssClasses = computed(() => ({
    'chip-cursor': props.notificationMessages !== undefined && props.notificationMessages?.length > 0 && props.activeSummary
}));
const modeChipCssClasses = computed(() => ({
    'mode-chip--fixed': props.fixedSize,
    'font-size-sm': props.fixedSize && (props.title?.length ?? 0) === 3,
    'font-size-xs': props.fixedSize && (props.title?.length ?? 0) === 4,
    'font-size-xxs': props.fixedSize && (props.title?.length ?? 0) >= 5
}));
</script>

<style lang="scss">
@use 'vuetify/settings' as *;

.mode-chip {
    --mode-chip-width: 24px;
    --mode-chip-height: 24px;
    --mode-chip-font-size: #{calc(16px / $font-size-root) * 1rem};

    padding: 0 3px;
    display: inline-flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    min-height: var(--mode-chip-height);
    min-width: var(--mode-chip-width);
    line-height: var(--mode-chip-font-size);
    font-size: var(--mode-chip-font-size);

    &.font-size-sm {
        --mode-chip-font-size: #{calc(14px / $font-size-root) * 1rem};
    }

    &.font-size-xs {
        --mode-chip-font-size: #{calc(12px / $font-size-root) * 1rem};
    }

    &.font-size-xxs {
        --mode-chip-font-size: #{calc(10px / $font-size-root) * 1rem};
    }

    &__title {
        display: inline-block;
        white-space: nowrap;
        font-weight: bold;

        .mode-chip--fixed & {
            white-space: normal;
            text-align: center;
        }
    }

    &--fixed {
        width: var(--mode-chip-width);
        height: var(--mode-chip-height);
    }

    + .v-badge__badge {
        padding: 0;
    }
}

.chip-cursor {
    cursor: pointer;
}
</style>
